interface WithRetriesOptions {
  maxAttempts?: number;
  onRetry: (error: unknown, attempts: number) => void;
  onRetriesExhausted: (error: unknown) => void;
}

/**
 * Wraps any function with retries.
 *
 * On success, will return a promise that resolves to the wrapped function's
 * return value. Note that even synchronous functions return promises.
 *
 * On failure, will call `onRetriesExhausted` with the error, which throws by
 * default.
 */
export default async function withRetries<Ret = unknown>(
  fn: () => Ret,
  options?: WithRetriesOptions,
  currAttempt = 1,
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
): Promise<Awaited<Ret>> {
  const {
    maxAttempts = 3,
    onRetry,
    onRetriesExhausted = (e: unknown) => {
      throw e;
    },
  } = options ?? {};

  try {
    return await Promise.resolve(fn());
  } catch (e) {
    if (currAttempt < maxAttempts) {
      onRetry?.(e, currAttempt);
      return await withRetries(fn, options, currAttempt + 1);
    } else {
      onRetriesExhausted(e);
    }
  }
}
